import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [message, setMessage] = useState("Confirming your new email…");

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("Missing confirmation token."); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/change-email/confirm", { token });
        if (cancelled) return;
        setStatus("ok");
        setMessage(`Your email is now ${data.email}. Please sign in again with your new email.`);
        refresh().catch(() => {});
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setMessage(formatErr(e));
      }
    })();
    return () => { cancelled = true; };
  }, [token, refresh]);

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-xl mx-auto" data-testid="verify-email-page">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Email verification</p>
      <h1 className="font-serif text-4xl mb-8">
        {status === "verifying" && "Confirming…"}
        {status === "ok" && "Email updated"}
        {status === "error" && "Couldn't confirm"}
      </h1>
      <div className={`bg-[#141414] p-6 border ${status === "ok" ? "border-gold/30" : status === "error" ? "border-red-500/40" : "border-[rgba(244,244,240,0.06)]"}`}>
        <div className="flex items-start gap-3">
          {status === "ok" && <CheckCircle2 size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />}
          {status === "error" && <AlertCircle size={20} strokeWidth={1.5} className="text-red-400 shrink-0 mt-0.5" />}
          <p className="text-sm text-muted leading-relaxed" data-testid="verify-email-message">{message}</p>
        </div>
      </div>
      <div className="mt-8 flex gap-3">
        <Link to="/login" className="btn-outline" data-testid="verify-email-signin">Sign in</Link>
        <Link to="/" className="btn-outline">Home</Link>
      </div>
    </div>
  );
}
